import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, FileQuestion } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { fetchJson, type Questionnaire, type QuestionnaireAfterAction } from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';

function QuestionnaireForm({
  initial,
  onSave,
  onCancel
}: {
  initial?: Partial<Questionnaire>;
  onSave: (data: Partial<Questionnaire>) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [ivrName, setIvrName] = useState(initial?.ivrName || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [afterAction, setAfterAction] = useState<QuestionnaireAfterAction>(
    initial?.afterAction || 'inherit'
  );
  const [afterExtension, setAfterExtension] = useState(initial?.afterExtension || '');
  const [busy, setBusy] = useState(false);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await onSave({
            name,
            ivrName,
            description,
            isActive,
            afterAction,
            afterExtension: afterAction === 'extension' ? afterExtension : null
          });
        } finally {
          setBusy(false);
        }
      }}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="name">שם השאלון *</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="למשל: שאלון מכירות"
        />
        <p className="text-xs text-muted-foreground">שם פנימי לאדמין בלבד</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="ivrName">שם להשמעה ב-IVR *</Label>
        <Input
          id="ivrName"
          value={ivrName}
          onChange={(e) => setIvrName(e.target.value)}
          required
          placeholder="למשל: שאלון מכירות"
        />
        <p className="text-xs text-muted-foreground">
          השם הזה יושמע ללקוח כאשר יש כמה שאלונים פעילים - "ל{ivrName || '...'} הקש 1"
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">תיאור</Label>
        <Textarea
          id="description"
          value={description || ''}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={isActive} onCheckedChange={setIsActive} id="isActive" />
        <Label htmlFor="isActive">פעיל</Label>
      </div>
      <div className="space-y-2">
        <Label>יעד לאחר השאלון</Label>
        <Select
          value={afterAction}
          onValueChange={(v) => setAfterAction(v as QuestionnaireAfterAction)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">ברירת המחדל של הארגון</SelectItem>
            <SelectItem value="hangup">ניתוק השיחה</SelectItem>
            <SelectItem value="main">חזרה לשלוחה ראשית</SelectItem>
            <SelectItem value="extension">מעבר לשלוחה ספציפית</SelectItem>
          </SelectContent>
        </Select>
        {afterAction === 'extension' && (
          <Input
            value={afterExtension}
            onChange={(e) => setAfterExtension(e.target.value)}
            dir="ltr"
            placeholder="מספר שלוחה, למשל 5 או 1/2"
          />
        )}
        <p className="text-xs text-muted-foreground">
          דורס את ההגדרה הכלל-ארגונית "יעד לאחר שאלון" עבור שאלון זה.
        </p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          ביטול
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? 'שומר...' : 'שמור'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function Questionnaires() {
  const confirm = useConfirm();
  const [items, setItems] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Questionnaire | null>(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchJson<Questionnaire[]>('/api/questionnaires');
      setItems(items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const columns: DataTableColumn<Questionnaire>[] = useMemo(
    () => [
      { key: 'name', header: 'שם', cell: (q) => <span className="font-medium">{q.name}</span>, exportValue: (q) => q.name },
      { key: 'ivrName', header: 'שם להשמעה', cell: (q) => q.ivrName, exportValue: (q) => q.ivrName },
      {
        key: 'activeQuestionsCount',
        header: 'שאלות פעילות',
        cell: (q) => q.activeQuestionsCount ?? 0,
        exportValue: (q) => q.activeQuestionsCount ?? 0
      },
      {
        key: 'status',
        header: 'סטטוס',
        cell: (q) => (
          <Badge variant={q.isActive ? 'success' : 'muted'}>{q.isActive ? 'פעיל' : 'מושבת'}</Badge>
        ),
        exportValue: (q) => (q.isActive ? 'פעיל' : 'מושבת')
      },
      {
        key: 'actions',
        header: 'פעולות',
        align: 'end',
        exportable: false,
        stopClickPropagation: true,
        cell: (q) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="icon" onClick={() => setEditing(q)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => {
                const ok = await confirm({
                  title: `למחוק את השאלון "${q.name}"?`,
                  description: 'אם יש sessions היסטוריים, ניתן רק להשבית את השאלון.',
                  confirmText: 'מחק',
                  variant: 'destructive'
                });
                if (!ok) return;
                try {
                  await fetchJson(`/api/questionnaires/${q.id}`, { method: 'DELETE' });
                  toast.success('נמחק');
                  refresh();
                } catch (err: unknown) {
                  const e = err as { message?: string };
                  if (e.message === 'has_sessions') {
                    toast.error('יש sessions היסטוריים. השבת במקום למחוק.');
                  } else {
                    toast.error(e.message || 'שגיאה');
                  }
                }
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        )
      }
    ],
    [confirm, refresh]
  );

  return (
    <div className="page-fill flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">שאלונים</h1>
          <p className="text-sm text-muted-foreground">
            סט שאלות שייאסף בשיחה. אם יש כמה פעילים - המתקשר יבחר באיזה לקלוט.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          הוסף שאלון
        </Button>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardContent className="flex min-h-0 flex-1 flex-col pt-6">
          <DataTable
            columns={columns}
            data={items}
            getRowKey={(q) => q.id}
            loading={loading}
            onRowClick={(q) => navigate(`/questionnaires/${q.id}`)}
            rowClassName={(q) => (!q.isActive ? 'opacity-50' : undefined)}
            exportFileName="שאלונים"
            exportSheetName="שאלונים"
            emptyMessage={
              <span className="flex flex-col items-center gap-2">
                <FileQuestion className="h-8 w-8 text-muted-foreground" />
                עדיין לא הוגדרו שאלונים. הוסף שאלון כדי להתחיל.
              </span>
            }
          />
        </CardContent>
      </Card>

      <Dialog
        open={creating || !!editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'עריכת שאלון' : 'יצירת שאלון'}</DialogTitle>
          </DialogHeader>
          <QuestionnaireForm
            initial={editing || undefined}
            onCancel={() => {
              setCreating(false);
              setEditing(null);
            }}
            onSave={async (data) => {
              try {
                if (editing) {
                  await fetchJson(`/api/questionnaires/${editing.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(data)
                  });
                  toast.success('עודכן');
                } else {
                  await fetchJson('/api/questionnaires', {
                    method: 'POST',
                    body: JSON.stringify(data)
                  });
                  toast.success('נוצר');
                }
                setCreating(false);
                setEditing(null);
                refresh();
              } catch (err: unknown) {
                const e = err as { message?: string };
                toast.error(e.message === 'name_exists' ? 'השם כבר קיים' : e.message || 'שגיאה');
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
